import { createSlice } from "@reduxjs/toolkit";
import {
  collapseAllCategories,
  expandAllCategories,
  resetCategoriesIds,
  resetTermsIds,
  toggleDictionaryCategory,
} from "utils/dictionary";
import {
  changeColour,
  syncCategories,
  validateCategory,
  validateDictionaryTerms,
} from "slices/dictionary/helpers";

export const initialState = {
  dictionaryList: [],
  edit: {
    editDictionary: null,
    unchangedDictionary: null,
    tableSettings: {
      isEditMode: false,
      draggingEntry: null,
      selectedRowIndex: 0,
      selectedColumnIndex: -1,
      selectedEntryType: null,
      isEditModeByDefaultForSelectedEntry: false,
      wildcardPopup: {
        displayed: false,
        term: null,
      },
    },
  },
};

export const dictionarySlice = createSlice({
  name: "dictionary",
  initialState,

  reducers: {
    getDictionaries: (state, action) => {
      state.dictionaryList = action.payload;
    },
    setDictionary: (state, action) => {
      state.edit.editDictionary = action.payload;
      state.edit.unchangedDictionary = action.payload;
    },
    cleanDictionary: () => {
      return initialState;
    },
    cancelEdits: (state) => {
      state.edit.editDictionary = { ...state.edit.unchangedDictionary };
    },
    toggleCategory: (state, action) => {
      state.edit.editDictionary = toggleDictionaryCategory(
        state.edit.editDictionary,
        action.payload.categoryId
      );
      state.edit.unchangedDictionary = toggleDictionaryCategory(
        state.edit.unchangedDictionary,
        action.payload.categoryId
      );
    },
    editDictionary: (state, action) => {
      const editFunction = action.payload;
      const newDictionary = editFunction(state.edit.editDictionary);

      state.edit.editDictionary = newDictionary;
      state.edit.unchangedDictionary = syncCategories(
        state.edit.unchangedDictionary,
        newDictionary
      );
    },
    editCategory: (state, action) => {
      const { categories } = state.edit.editDictionary;
      state.edit.editDictionary.categories = categories.map((category) => {
        if (category.id === action.payload.categoryId) {
          return { ...action.payload.editedCategory };
        }
        return category;
      });
    },
    editTerm: (state, action) => {
      const { categories } = state.edit.editDictionary;
      state.edit.editDictionary.categories = categories.map((category) => {
        if (category.id === action.payload.categoryId) {
          return {
            ...category,
            terms: category.terms.map((term) => {
              if (term.id === action.payload.termId) {
                return { ...action.payload.editedTerm };
              }
              return term;
            }),
          };
        }
        return category;
      });
    },
    addTerm: (state, action) => {
      const { categories } = state.edit.editDictionary;
      state.edit.editDictionary.categories = categories.map((category) => {
        if (category.id === action.payload.categoryId) {
          let terms = [...category.terms];
          terms.splice(action.payload.indexToPlace, 0, action.payload.term);
          terms = resetTermsIds(terms, category.id);
          return {
            ...category,
            isExpanded: true,
            terms,
          };
        }
        return category;
      });
    },
    addCategory: (state, action) => {
      let categories = [...state.edit.editDictionary.categories];

      categories.splice(
        action.payload.indexToPlace,
        0,
        action.payload.category
      );
      categories = resetCategoriesIds(categories);

      state.edit.editDictionary = { ...state.edit.editDictionary, categories };
    },
    deleteTerm: (state, action) => {
      state.edit.editDictionary.categories =
        state.edit.editDictionary.categories.map((category) => {
          if (category.id === action.payload.categoryId) {
            let terms = [...category.terms];
            terms = terms.filter((term) => term.id !== action.payload.termId);
            terms = resetTermsIds(terms, category.id);
            return {
              ...category,
              terms,
            };
          }
          return category;
        });
    },
    deleteCategory: (state, action) => {
      let categories = [...state.edit.editDictionary.categories];

      categories = categories.filter(
        (category) => category.id !== action.payload.categoryId
      );
      categories = resetCategoriesIds(categories);

      state.edit.editDictionary = { ...state.edit.editDictionary, categories };
    },
    setModeToEdit: (state) => {
      state.edit.tableSettings.isEditMode = true;
    },
    setModeToView: (state) => {
      state.edit.tableSettings.isEditMode = false;
    },
    setDraggingEntry: (state, action) => {
      state.edit.tableSettings.draggingEntry = action.payload.draggingEntry;
    },
    setSelectedRowIndex: (state, action) => {
      state.edit.tableSettings.selectedRowIndex = action.payload.rowIndex;
    },
    setSelectedEntry: (state, action) => {
      state.edit.tableSettings.selectedEntry = action.payload;
    },
    setSelectedColumnIndex: (state, action) => {
      state.edit.tableSettings.selectedColumnIndex = action.payload.columnIndex;
    },
    setDefaultMode: (state, action) => {
      state.edit.tableSettings.isEditModeByDefaultForSelectedEntry =
        action.payload.isEditMode;
    },
    expandCategories: (state) => {
      state.edit.editDictionary.categories = expandAllCategories(
        state.edit.editDictionary.categories
      );
      state.edit.unchangedDictionary.categories = expandAllCategories(
        state.edit.editDictionary.categories
      );
    },
    collapseCategories: (state) => {
      state.edit.editDictionary.categories = collapseAllCategories(
        state.edit.editDictionary.categories
      );
      state.edit.unchangedDictionary.categories = collapseAllCategories(
        state.edit.editDictionary.categories
      );
    },
    displayWildcardPopup: (state, action) => {
      state.edit.tableSettings.wildcardPopup = {
        displayed: action.payload.displayed,
        term: action.payload.term,
      };
    },
    setLocked: (state, action) => {
      state.edit.editDictionary.locked = action.payload;
    },
    validateCategories: (state) => {
      state.edit.editDictionary.categories =
        state.edit.editDictionary.categories.map((category) => {
          const { categories } = state.edit.editDictionary;
          return validateCategory(category, categories);
        });
    },
    validateTerms: (state) => {
      state.edit.editDictionary.categories =
        state.edit.editDictionary.categories.map((category) => {
          const { categories } = state.edit.editDictionary;
          return {
            ...category,
            terms: validateDictionaryTerms(category, categories),
          };
        });
    },
    changeCategoryColour: (state, action) => {
      state.edit.editDictionary.categories =
        state.edit.editDictionary.categories.map((category) => {
          return changeColour(category, action);
        });
    },
  },
});

export const {
  getDictionaries,
  setDictionary,
  cleanDictionary,
  cancelEdits,
  toggleCategory,
  editDictionary,
  editCategory,
  editTerm,
  addTerm,
  addCategory,
  deleteTerm,
  deleteCategory,
  setModeToEdit,
  setModeToView,
  setDraggingEntry,
  setSelectedRowIndex,
  setSelectedEntry,
  setSelectedColumnIndex,
  setDefaultMode,
  expandCategories,
  collapseCategories,
  displayWildcardPopup,
  setLocked,
  validateCategories,
  validateTerms,
  changeCategoryColour,
} = dictionarySlice.actions;

export default dictionarySlice.reducer;
